import { Router } from '@angular/router';
import { Injectable, inject } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ToastService } from '@services/toast.service';
import { UtilsService } from '../services/utils.service';
import { FeedEntityService } from '../services/feed-entity.service';
import { WidgetApiService } from 'src/app/api/queries/widget-api.service';
import { CurrentViewWidgetsService } from '../services/current-view-widgets.service';

import { FeedResultsPreviewDialogComponent } from '../components/popups/feed-results-preview-dialog/feed-results-preview-dialog.component';

import { DialogCloseReason, DialogService } from '@sui/dialog';

import {
  Gap,
  toUnix,
  fromUnix,
  getPeriodBounds,
} from 'src/app/shared/utils/date-utils';
import { Dimension, FilterItem, PeriodFilterItem } from '../models/core.models';

import {
  activateFilters,
  selectFilterItem,
  deselectFilterItem,
  toggleFilterDimension,
} from '../actions/feed.actions';
import { periodChanged } from '../actions/core.actions';
import * as widgetActions from '../actions/widget.actions';

import {
  getContentFilters,
  selectCurrentObject,
  selectFeedActiveFilterItems,
} from '../reducers';
import { AnyFilterItem, isValueItem } from '../reducers/feed.reducer';

import { of, concat, from, EMPTY } from 'rxjs';
import {
  map,
  take,
  filter,
  mergeMap,
  switchMap,
  catchError,
} from 'rxjs/operators';

// ! Widget writes (and their cache patches) live in `WidgetPersistenceService`;
// ! this file is the widget-click → feed-filter choreography only.

@Injectable()
export class WidgetEffects {
  private readonly widgetApi = inject(WidgetApiService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);
  private readonly feedEntity = inject(FeedEntityService);

  activatePivotFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.activatePivotFeed),
      switchMap((action) =>
        this.feedEntity.getFeed(action.pivotFeedId).pipe(
          map((feed) => {
            return toggleFilterDimension({
              dimension: feed as Dimension,
              activate: false,
              isPivot: true,
            });
          }),
          catchError(() => {
            this.toastService.showError('Activate Pivot Feed Failed');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  widgetClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.htmlWidgetClicked),
      map(({ payload }) => payload),
      map((record) => {
        const widgetId = record.widget_id as string;
        const propertyUri = record.property_uri as string;
        const rawValue = record[record.property_uri];
        const canUse =
          typeof rawValue === 'number' || typeof rawValue === 'string';

        const filterItem: AnyFilterItem | null = canUse
          ? {
              checked: true,
              kind: 'value',
              count_posts: record.y,
              property_uri: propertyUri,
              raw_value: String(rawValue),
              title: record.name,
              active: false,
              type: 'include',
            }
          : null;

        return { widgetId, propertyUri, filterItem };
      }),
      map(({ widgetId, propertyUri, filterItem }) => ({
        propertyUri,
        filterItem,
        widget: this.currentViewWidgets.byId(widgetId),
      })),
      mergeMap(({ propertyUri, filterItem, widget }) => {
        const out: Action[] = [];

        if (filterItem) {
          out.push(
            selectFilterItem({
              propertyUri,
              filterItem,
              mode: 'include',
            }),
          );
        }

        const widgetFilterDimensions = widget?.dimensions_filter ?? [];
        if (widgetFilterDimensions.length) {
          for (const filterDimension of widgetFilterDimensions) {
            out.push(
              toggleFilterDimension({
                dimension: filterDimension,
                activate: true,
                isPivot: false,
              }),
            );
          }
        }
        out.push(activateFilters());

        return out;
      }),
    ),
  );

  widgetPointClickedResolve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClicked),

      map(({ widgetId, options }) => ({
        widgetId,
        options,
        widget: this.currentViewWidgets.byId(widgetId),
      })),

      switchMap(({ widgetId, options, widget }) => {
        if (widget) {
          return of(
            widgetActions.widgetPointClickedResolved({
              widgetId,
              options,
              widget,
            }),
          );
        }

        // * the clicked widget is missing from the current view's list only when
        // * the click comes from another view's widget (cross-feed point click)
        return this.widgetApi.getWidget(widgetId).pipe(
          map((fetched) =>
            widgetActions.widgetPointClickedResolved({
              widgetId,
              options,
              widget: fetched,
            }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  widgetPointClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClickedResolved),

      concatLatestFrom(() => [
        this.store.select(selectFeedActiveFilterItems),
        this.store.select(getContentFilters),
        this.store.select(selectCurrentObject),
      ]),

      mergeMap(
        ([
          { widgetId, options: pointOptions, widget },
          items,
          contentFilters,
          currentObject,
        ]) => {
          const {
            datetime: isDateTime,
            name: dateValue,
            value_name: valueName,
            raw: rawValue,
            property_uri: propertyUri,
            pivot_query_id: pivotFeedId,
            zoom_start: zoomStart,
            zoom_end: zoomEnd,
            is_reset_zoom: isResetZoom,
          } = pointOptions as any;

          const isZoom = zoomStart !== undefined && zoomEnd !== undefined;

          const datasourceIds = widget?.datasources?.map((ds) => ds.id) ?? [];

          const pointDsId = (pointOptions as any).dsid;
          const targetFeedId = pointDsId || datasourceIds[0];
          const hasDifferentDatasource =
            targetFeedId && currentObject?.id !== targetFeedId;

          const actions: Action[] = [];

          const isPeriodField = UtilsService.isPeriodField(propertyUri || '');
          const shouldDispatchFilter =
            propertyUri && !isPeriodField && !pivotFeedId;

          if (!isZoom && shouldDispatchFilter) {
            const filterItem: FilterItem = {
              title: valueName,
              kind: 'value',
              raw_value: rawValue,
              count_posts: 0,
              icon: 'filter_icon',
              checked: true,
              active: false,
              property_uri: propertyUri,
              type: 'include',
            };

            actions.push(
              selectFilterItem({ propertyUri, filterItem, mode: 'include' }),
            );
          }
          if (widget?.dimensions_filter?.length) {
            for (const dimension of widget.dimensions_filter) {
              actions.push(
                toggleFilterDimension({
                  dimension,
                  activate: false,
                  isPivot: false,
                }),
              );
            }
          }

          if (isZoom) {
            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const start: Date = fromUnix(zoomStart);
            const end: Date = fromUnix(zoomEnd);

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ?? `${start.toISOString()} - ${end.toISOString()}`,
              time_start: zoomStart,
              time_end: zoomEnd,
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            if (!isResetZoom) {
              const cfStart = contentFilters?.time_start;
              const cfEnd = contentFilters?.time_end;

              if (
                typeof cfStart === 'number' &&
                typeof cfEnd === 'number' &&
                (zoomStart < cfStart || zoomEnd > cfEnd)
              ) {
                actions.push(
                  periodChanged({
                    period: {
                      startTimestamp: Math.min(cfStart, zoomStart),
                      endTimestamp: Math.max(cfEnd, zoomEnd),
                    },
                  }),
                );
              }

              actions.push(
                selectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            } else {
              actions.push(
                deselectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            }
          } else if (isDateTime) {
            const currentGap: Gap =
              (widget.settings?.['period_gap'] as Gap) || '+1DAY';
            const { startDate, endDate } = getPeriodBounds(
              dateValue,
              currentGap,
            );

            const clickedStart = toUnix(startDate);
            const clickedEnd = toUnix(endDate);

            const cfStart = contentFilters?.time_start; // unix
            const cfEnd = contentFilters?.time_end; // unix

            // If clicked period is outside current contentFilters range -> expand range
            if (
              typeof cfStart === 'number' &&
              typeof cfEnd === 'number' &&
              (clickedStart < cfStart || clickedEnd > cfEnd)
            ) {
              actions.push(
                periodChanged({
                  period: {
                    startTimestamp: Math.min(cfStart, clickedStart),
                    endTimestamp: Math.max(cfEnd, clickedEnd),
                  },
                }),
              );
            }

            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ??
                `${startDate.toISOString()} - ${endDate.toISOString()}`,
              time_start: toUnix(startDate),
              time_end: toUnix(endDate),
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            actions.push(
              selectFilterItem({
                propertyUri,
                filterItem: periodFilterItem,
                mode: 'include',
              }),
            );
          }

          if (pivotFeedId) {
            actions.push(
              widgetActions.activatePivotFeed({ widgetId, pivotFeedId }),
            );
          }

          const isNewItem =
            shouldDispatchFilter &&
            !items.some(
              (item) =>
                isValueItem(item) &&
                item.property_uri === propertyUri &&
                item.raw_value === rawValue,
            );

          const shouldActivateFilters =
            isNewItem || items.length === 0 || isDateTime || !!pivotFeedId;

          if (hasDifferentDatasource) {
            const pointFilters = {
              ...(contentFilters || {}),
            };

            const isPeriodField = UtilsService.isPeriodField(propertyUri || '');

            // Value filter from point click
            if (
              propertyUri &&
              rawValue !== undefined &&
              !isPeriodField &&
              !pivotFeedId
            ) {
              pointFilters[propertyUri] = { include: [rawValue], exclude: [] };
            }

            // Period filter from datetime point click
            if (isDateTime) {
              const currentGap: Gap =
                (widget.settings?.['period_gap'] as Gap) || '+1DAY';
              const { startDate, endDate } = getPeriodBounds(
                dateValue,
                currentGap,
              );
              const periodField = isPeriodField
                ? propertyUri
                : 'post_time_published';
              pointFilters[periodField] = {
                include: [{ start: toUnix(startDate), end: toUnix(endDate) }],
                exclude: [],
              };
            }

            const dialogRef = this.dialogService.openSimpleDialog(
              FeedResultsPreviewDialogComponent,
              {
                data: {
                  feedId: targetFeedId,
                  contentFilters: {
                    time_start: pointFilters?.time_start,
                    time_end: pointFilters?.time_end,
                    sort: pointFilters?.sort,
                    order: pointFilters?.order,
                    ...(Object.keys(pointFilters).length > 0
                      ? { filters: pointFilters }
                      : {}),
                  },

                  pivotFeedId: pivotFeedId,
                  filterDimensions: widget?.dimensions_filter ?? [],
                  filterLabel: valueName,
                },
                size: 'large',
              },
            );

            return dialogRef.closed.pipe(
              take(1),
              switchMap((event) => {
                if (
                  event.reason !== DialogCloseReason.Confirm ||
                  !(event.data as any)?.navigate
                ) {
                  return EMPTY;
                }

                const workspaceId = (event.data as any).workspaceId;

                // Navigate first; dispatch the filter actions only after the
                // navigation succeeds, so they hit the target feed's state.
                return from(
                  this.router.navigate([
                    'analytics',
                    'workspace',
                    workspaceId,
                    'feed',
                    targetFeedId,
                  ]),
                ).pipe(
                  filter((success) => success),
                  switchMap(() => {
                    if (pivotFeedId && shouldActivateFilters) {
                      return concat(
                        from(actions),
                        this.actions$.pipe(
                          ofType(toggleFilterDimension),
                          filter((action) => action.isPivot === true),
                          take(1),
                          map(() => activateFilters()),
                        ),
                      );
                    }

                    if (shouldActivateFilters) {
                      actions.push(activateFilters());
                    }

                    return from(actions);
                  }),
                );
              }),
            );
          }

          // Pivot case: activateFilters must wait for the pivot feed to load -
          // its toggleFilterDimension (isPivot) is the "loaded" signal.
          if (pivotFeedId && shouldActivateFilters) {
            return concat(
              from(actions),
              this.actions$.pipe(
                ofType(toggleFilterDimension),
                filter((action) => action.isPivot === true),
                take(1),
                map(() => activateFilters()),
              ),
            );
          }

          if (shouldActivateFilters) {
            actions.push(activateFilters());
          }

          return actions;
        },
      ),
    ),
  );
  constructor(
    private actions$: Actions,
    private store: Store,
    private toastService: ToastService,
    private dialogService: DialogService,
    private router: Router,
  ) {}
}
